import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Sparkles, ArrowDown } from "lucide-react";
import { WEDDING } from "@/lib/constants";
import RSVPForm from "@/components/RSVPForm";
import EditableImage from "@/components/EditableImage";

// Background images for STORIA and BREVE blocks
const STORIA_BG = "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/bnh4jit2_storia.jpg";
const BREVE_BG = "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/01ga8032_breve.jpg";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/62xg5boj_splash-desktop.webp";

function Countdown() {
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    // Target: 18 September 2026 at 16:00 Italian time (CEST = UTC+2)
    // Using explicit UTC time: 16:00 CEST = 14:00 UTC
    const target = Date.UTC(2026, 8, 18, 14, 0, 0); // Month is 0-indexed, so 8 = September
    
    const tick = () => {
      const now = Date.now();
      const diff = Math.max(0, target - now);
      setTime({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff / 3600000) % 24),
        minutes: Math.floor((diff / 60000) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n) => String(n).padStart(2, '0');
  return (
    <div className="flex items-center justify-center gap-3 sm:gap-5 md:gap-8" data-testid="countdown">
      {[
        { v: time.days, l: "giorni" },
        { v: time.hours, l: "ore" },
        { v: time.minutes, l: "min" },
        { v: time.seconds, l: "sec" },
      ].map(({ v, l }) => (
        <div key={l} className="text-center">
          <div className="font-heading text-3xl sm:text-4xl md:text-5xl text-w-charcoal">{pad(v)}</div>
          <div className="font-body text-[9px] sm:text-[10px] md:text-xs text-w-muted uppercase tracking-widest mt-1">{l}</div>
        </div>
      ))}
    </div>
  );
}

export default function SplashScreen() {
  const navigate = useNavigate();
  const scrollToRsvp = () => document.getElementById('splash-rsvp')?.scrollIntoView({ behavior: 'smooth' });

  // Set page title
  useEffect(() => {
    document.title = "Lelia & Stefano in matrimonio";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-w-cream" data-testid="splash-screen">
      {/* Header Text Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center py-8 md:py-12 px-6"
      >
        <p className="font-heading text-4xl sm:text-5xl lg:text-[52px] font-light text-w-charcoal tracking-wider">Matrimonio di</p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light text-w-charcoal leading-tight">
          {WEDDING.sposa} <span className="font-accent italic text-w-gold">&</span> {WEDDING.sposo}
        </h1>
        <p className="font-body text-sm md:text-base text-w-muted mt-2 tracking-wide">
          Borgio Verezzi (SV), Liguria
        </p>
        <div className="w-12 h-px bg-w-gold mx-auto mt-5" />
      </motion.div>

      {/* Countdown Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        className="text-center px-6 pb-12 md:pb-16"
      >
        {/* Date/Time Box with gold border */}
        <div 
          className="inline-block px-8 py-4 md:px-12 md:py-5 mb-8 md:mb-10"
          style={{ border: '1px solid #C1A88B' }}
        >
          <p className="font-body text-xs md:text-sm tracking-[0.4em] uppercase text-w-sage font-bold">
            Venerdi' {WEDDING.data_short}
          </p>
          <p className="font-body text-xs md:text-sm tracking-[0.4em] uppercase text-w-sage font-bold mt-1">{WEDDING.orario}</p>
        </div>
        
        <p className="font-body text-xs tracking-[0.3em] uppercase text-w-charcoal mb-4">Mancano solo...</p>
        <Countdown />
        <button
          onClick={scrollToRsvp}
          data-testid="splash-rsvp-cta"
          className="mt-12 md:mt-16 inline-flex items-center gap-2 bg-w-sage text-white font-body text-xs uppercase tracking-widest px-8 py-3 rounded-full btn-wedding hover:bg-w-sage/90 transition-colors"
        >
          Dicci se ci sarai
          <ArrowDown className="w-3 h-3" />
        </button>
      </motion.div>

      {/* Full-width Hero Image */}
      {/* Mobile: full width, square aspect */}
      <div className="md:hidden w-full aspect-square max-h-[85vh] overflow-hidden">
        <img 
          src={HERO_IMAGE}
          alt="Lelia e Stefano" 
          className="w-full h-full object-cover object-center"
        />
      </div>
      {/* Desktop: narrower width, taller height, crop from bottom */}
      <div className="hidden md:block w-[90%] lg:w-[85%] mx-auto aspect-[16/10.5] lg:aspect-[2/1.15] max-h-[90vh] overflow-hidden">
        <img 
          src={HERO_IMAGE}
          alt="Lelia e Stefano" 
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Personal message */}
      <div className="py-10 md:py-14 px-6 bg-w-cream">
        <div className="max-w-xl mx-auto text-center">
          <p className="font-accent italic text-sm md:text-base text-w-muted leading-relaxed">
            Il solo pensiero di questo giorno ci emoziona e ci riempirebbe di gioia sapere di essere circondati dalle persone che più amiamo, che più fanno parte della nostra vita e della nostra fortuna, come te.
          </p>
          <p className="font-accent italic text-sm md:text-base text-w-muted leading-relaxed mt-4">
            Scegli come scoprire i vari dettagli e facci sapere se potrai partecipare.<br />
            Grazie mille!
          </p>
          <p className="font-accent italic text-sm md:text-base text-w-muted leading-relaxed mt-4">
            Con affetto
          </p>
          <p className="font-heading text-lg md:text-xl text-w-charcoal mt-2">
            Lelia & Stefano
          </p>
        </div>
      </div>

      {/* STORIA + BREVE Blocks - STORIA first */}
      <div className="flex flex-col md:flex-row gap-[7px] min-h-[40vh] md:min-h-[52vh] px-0 md:px-0">
        {/* STORIA - First (gold glitter background - with center gradient for readability) */}
        <div
          className="relative flex-1 min-h-[28vh] md:min-h-0 cursor-pointer overflow-hidden group"
          onClick={() => navigate('/storia')}
          data-testid="splash-story-btn"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/storia')}
        >
          {/* Background Image */}
          <img 
            src={STORIA_BG} 
            alt="" 
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          {/* Radial gradient overlay - darker in center for text readability */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.55)_0%,_rgba(0,0,0,0.2)_70%,_rgba(0,0,0,0.1)_100%)] opacity-90" />
          
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="relative h-full flex flex-col items-center justify-center p-8 z-10"
          >
            <Sparkles className="w-6 h-6 mb-3 text-white" strokeWidth={1.5} />
            <h2 className="font-accent italic text-2xl sm:text-3xl md:text-4xl tracking-wide text-white drop-shadow-lg whitespace-nowrap">Raccontami una storia</h2>
            <p className="font-body text-sm mt-2 max-w-xs text-center text-white drop-shadow-md">Ho tutto il tempo del mondo. Sorprendimi.</p>
            <div className="mt-4 flex items-center gap-2 text-xs font-body uppercase tracking-widest text-white">
              <span>Entra</span><ArrowRight className="w-3.5 h-3.5" />
            </div>
          </motion.div>
        </div>

        {/* BREVE - Second (wood texture background) */}
        <div
          className="relative flex-1 min-h-[28vh] md:min-h-0 cursor-pointer overflow-hidden group"
          onClick={() => navigate('/breve')}
          data-testid="splash-brief-btn"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/breve')}
        >
          {/* Background Image */}
          <img 
            src={BREVE_BG} 
            alt="" 
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          {/* Gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
          
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="relative h-full flex flex-col items-center justify-center p-8 z-10"
          >
            <Zap className="w-6 h-6 mb-3 text-white" strokeWidth={1.5} />
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl tracking-wide text-white drop-shadow-lg whitespace-nowrap">Dimmelo in breve</h2>
            <p className="font-body text-sm mt-2 max-w-xs text-center text-white drop-shadow-md">Vai, che ho fretta. Le info essenziali, dritto al punto.</p>
            <div className="mt-4 flex items-center gap-2 text-xs font-body uppercase tracking-widest text-white">
              <span>Entra</span><ArrowRight className="w-3.5 h-3.5" />
            </div>
          </motion.div>
        </div>
      </div>

      <div id="splash-rsvp" className="bg-w-cream">
        <RSVPForm />
      </div>
    </div>
  );
}
